import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../database/config';
import { useHomeData } from '../../../database/HomeDataContext';
import { Body9Skeleton } from '../../Skeletons/Skeletons';
import { FaGooglePlay, FaApple } from 'react-icons/fa';

const Body9 = ({ preloadedData }) => {
  const { data: homeData, isOutsideProvider } = useHomeData();
  const [localData, setLocalData] = useState(null);

  // Fallback fetch for when used outside HomeDataProvider (e.g. About page)
  useEffect(() => {
    if (!isOutsideProvider) return;
    const fetchBody9Data = async () => {
      try {
        const contentSnap = await getDoc(doc(db, 'home', 'orangeBottomBar'));
        if (contentSnap.exists()) {
          const data = contentSnap.data();
          setLocalData({
            tag: data.totalMeals || '',
            heading: data.heading1 || '',
            subHeading: data.subHeading || '',
            androidLink: data.androidLink || 'https://play.google.com/store/apps/details?id=com.home_meals_1&hl=en_IN',
            iosLink: data.iosLink || 'https://apps.apple.com/in/app/bentobox-home-cooked-meals/id6749816927',
            image: data.img || '',
          });
        }
      } catch (error) {
        console.error('Error fetching Body9 data:', error);
      }
    };
    fetchBody9Data();
  }, [isOutsideProvider]);

  const body9Data = preloadedData || (isOutsideProvider ? localData : homeData?.body9);

  if (!body9Data) {
    return <Body9Skeleton />;
  }

  return (
    <section className="pt-10 sm:pt-12 lg:pt-16 px-6 sm:px-10 md:px-16 lg:px-20 xl:px-32 bg-orange-50/50 overflow-hidden">
      {/* Content Container */}
      <div className="max-w-6xl mx-auto text-center">
        {/* Tag Badge */}
        <div className="inline-flex items-center gap-2 bg-orange-500 text-white text-xs sm:text-sm font-semibold px-4 py-2 rounded-full mb-6">
          {body9Data.tag}
        </div>

        {/* Main Heading */}
        <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
          {body9Data.heading}
        </h2>

        {/* Subheading */}
        <p className="text-gray-600 text-base sm:text-lg max-w-xl mx-auto mb-8">
          {body9Data.subHeading}
        </p>

        {/* Download Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-12 sm:mb-16">
          {/* Android Button */}
          <a
            href={body9Data.androidLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-gray-900 hover:bg-gray-800 text-white font-medium px-6 py-3 rounded-full transition-colors duration-200"
          >
            <FaGooglePlay className="text-lg" />
            <span>Download for Android</span>
          </a>

          {/* iOS Button */}
          <a
            href={body9Data.iosLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-900 font-medium px-6 py-3 rounded-full border border-gray-300 transition-colors duration-200"
          >
            <FaApple className="text-xl" />
            <span>Download for iOS</span>
          </a>
        </div>

        {/* App Image */}
        {body9Data.image && (
          <div className="max-w-4xl mx-auto">
            <img
              src={body9Data.image}
              alt="BentoBox App - Home Cooked Meals Delivery"
              title="BentoBox App - Order Home Cooked Meals"
              width={800}
              height={500}
              loading="lazy"
              decoding="async"
              className="w-full h-auto object-contain"
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default Body9;
